import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Flame,
  Sparkles,
  ArrowRight,
  Calendar,
  Tag,
  TrendingDown,
  Clock,
  Loader2,
  Briefcase,
  Tractor,
  Home as HomeIcon,
  Users2,
  CheckCircle2,
  ShieldCheck,
  Phone,
  Sun,
  CalendarDays,
  Trophy,
  IndianRupee,
} from "lucide-react";
import { PolicyPageShell } from "@/components/PolicyHeader";
import { Offers } from "@/lib/api";

const FESTIVAL_CAMPAIGNS = [
  "Diwali Special",
  "Navratri Festival Offers",
  "New Year Celebration",
  "Holi Special Benefits",
  "Raksha Bandhan Offers",
  "Independence Day Campaign",
  "Republic Day Campaign",
  "Christmas & Year-End",
];

const STATIC_CATEGORIES = [
  {
    icon: Briefcase,
    title: "Business Growth Offers",
    accent: "#D4AF37",
    items: [
      "MSMEs",
      "Startups",
      "Manufacturing Units",
      "Traders & Retail",
      "Service Industries",
      "Agricultural Businesses",
      "Infrastructure Projects",
    ],
    note: "Benefits may include faster processing, business advisory and customized funding.",
  },
  {
    icon: Tractor,
    title: "Agriculture & Rural Development",
    accent: "#7CB342",
    items: [
      "Agriculture Equipment",
      "Irrigation Systems",
      "Farm Infrastructure",
      "Dairy & Poultry Projects",
      "Greenhouse / Polyhouse",
      "Agri Business Expansion",
    ],
    note: "Helping farmers and rural entrepreneurs build long-term capacity.",
  },
  {
    icon: HomeIcon,
    title: "Personal Financial Solutions",
    accent: "#5DADE2",
    items: [
      "Home Improvement",
      "Education Planning",
      "Medical Requirements",
      "Marriage Planning",
      "Family Financial Needs",
      "Professional Development",
      "Asset Purchase",
    ],
    note: "Exclusive seasonal support for personal milestones.",
  },
];

const EXCLUSIVE_BENEFITS = [
  "Priority Application Processing",
  "Personalized Financial Consultation",
  "Dedicated Relationship Manager",
  "Customized Financial Solutions",
  "Business Advisory Sessions",
  "Investment Planning Guidance",
  "Project Evaluation Assistance",
  "Digital Documentation Support",
  "Special Partner Rewards",
  "Referral Benefits",
];

const PARTNER_REWARDS = [
  "Attractive Commission Structure",
  "Performance-Based Incentives",
  "Festival Reward Programs",
  "Recognition Awards",
  "Leadership Growth Opportunities",
  "Professional Training & Certification",
  "Business Development Support",
  "Marketing Assistance",
];

const WHY_SEASONAL = [
  "Limited-Time Exclusive Benefits",
  "Transparent Process",
  "Customer-Focused Solutions",
  "Professional Financial Guidance",
  "Fast & Reliable Support",
  "Trusted Financial Partner",
  "Personalized Assistance",
  "Ethical & Transparent Practices",
];

export default function HotOffers() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Offers.list()
      .then((data) => setOffers(data || []))
      .catch(() => setOffers([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <PolicyPageShell
      testId="hot-offers-page"
      eyebrow="Limited Time"
      title={
        <>
          Hot <em className="not-italic text-[#D4AF37]">Offers</em> &amp;{" "}
          <span className="font-script italic text-[#F0D788]">Festival Specials</span>
        </>
      }
      lead="Celebrate every occasion with exclusive financial benefits. We introduce Limited-Time Offers and Festival Specials so individuals, families, entrepreneurs and businesses can achieve their financial goals with greater ease and value."
    >
      {/* Festival campaigns */}
      <section data-testid="festival-campaigns" className="mb-16">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">
          Festival Finance Offers
        </div>
        <h2 className="font-serif text-2xl md:text-3xl text-white mb-5">
          Exclusive financial solutions during every major festival
        </h2>
        <div className="flex flex-wrap gap-2">
          {FESTIVAL_CAMPAIGNS.map((f) => (
            <span
              key={f}
              data-testid={`festival-${f.replace(/\W+/g, "-").toLowerCase()}`}
              className="px-3 py-1.5 rounded-full border border-[#D4AF37]/25 bg-[#0A1F44] text-xs text-white/85"
            >
              {f}
            </span>
          ))}
        </div>
        <p className="mt-4 text-xs text-white/55 italic">
          Offer availability may vary depending on the season.
        </p>
      </section>

      {/* Category-wise offers */}
      <section data-testid="category-offers" className="mb-16">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">
          Category-wise Special Support
        </div>
        <h2 className="font-serif text-2xl md:text-3xl text-white mb-8">
          Tailored seasonal benefits for every customer segment
        </h2>
        <div className="grid md:grid-cols-3 gap-5">
          {STATIC_CATEGORIES.map((c, i) => (
            <div
              key={c.title}
              data-testid={`category-${i}`}
              className="rounded-2xl border border-[#D4AF37]/15 bg-[#0A1F44] p-6"
            >
              <div
                className="w-10 h-10 grid place-items-center rounded-lg mb-4"
                style={{
                  background: `${c.accent}18`,
                  border: `1px solid ${c.accent}40`,
                }}
              >
                <c.icon size={18} style={{ color: c.accent }} />
              </div>
              <h3 className="font-serif text-lg text-white mb-3">{c.title}</h3>
              <ul className="space-y-1.5 mb-4">
                {c.items.map((it) => (
                  <li key={it} className="flex items-start gap-2 text-sm text-white/75">
                    <CheckCircle2 size={12} className="text-[#D4AF37] mt-1 shrink-0" /> {it}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-white/55 italic leading-relaxed">{c.note}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Exclusive benefits */}
      <section data-testid="exclusive-benefits" className="mb-16">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">
          Exclusive Benefits
        </div>
        <h2 className="font-serif text-2xl md:text-3xl text-white mb-5">
          During promotional campaigns, eligible applicants may receive
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {EXCLUSIVE_BENEFITS.map((b, i) => (
            <div
              key={b}
              data-testid={`exclusive-${i}`}
              className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3"
            >
              <Sparkles size={14} className="text-[#D4AF37] mt-1 shrink-0" />
              <div className="text-sm text-white/85">{b}</div>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-white/55 italic">
          Benefits are subject to eligibility and company policies.
        </p>
      </section>

      {/* DSA / Partner rewards */}
      <section data-testid="partner-rewards" className="mb-16">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">
          DSA &amp; Associate Partner Rewards
        </div>
        <h2 className="font-serif text-2xl md:text-3xl text-white mb-5">
          Become part of our growing financial network
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {PARTNER_REWARDS.map((r, i) => (
            <div
              key={r}
              data-testid={`partner-reward-${i}`}
              className="flex items-start gap-3 rounded-xl border border-[#D4AF37]/15 bg-[#0A1F44] px-4 py-3"
            >
              <Users2 size={14} className="text-[#F0D788] mt-1 shrink-0" />
              <div className="text-sm text-white/85">{r}</div>
            </div>
          ))}
        </div>
        <div className="mt-6">
          <Link
            to="/careers"
            data-testid="offers-careers-cta"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full btn-outline-gold text-sm"
          >
            Join the Partner Program <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* DSA Monthly Achievements — Target Benefit */}
      <DsaAchievements />

      {/* Why seasonal */}
      <section data-testid="why-seasonal" className="mb-16">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">
          Why Choose Our Seasonal Offers
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {WHY_SEASONAL.map((w, i) => (
            <div
              key={w}
              data-testid={`why-seasonal-${i}`}
              className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3"
            >
              <ShieldCheck size={13} className="text-[#D4AF37] mt-1 shrink-0" />
              <div className="text-sm text-white/85">{w}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Apply CTA */}
      <section className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#0A1F44] via-[#0c2654] to-[#04102B] p-8 md:p-12 text-center">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#F0D788] mb-3">
          📞 Apply Today
        </div>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
          Exclusive offers today.{" "}
          <span className="font-script italic text-[#F0D788]">Stronger financial tomorrow.</span>
        </h2>
        <p className="text-base text-white/70 mb-7 max-w-2xl mx-auto">
          Don't miss the opportunity. Our financial experts are ready to help you choose the most
          suitable solution for your personal or business needs.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <a
            href="tel:+917016975324"
            data-testid="offers-call-cta"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full btn-gold text-sm font-semibold"
          >
            <Phone size={14} /> Call +91 70169 75324
          </a>
          <Link
            to="/apply/loan"
            data-testid="offers-apply-cta"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full btn-outline-gold text-sm"
          >
            Apply Online <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* Disclaimer */}
      <div className="mt-10 text-[11px] text-white/45 leading-relaxed">
        <strong className="text-white/65">Disclaimer:</strong> All offers, benefits and
        promotional programs are subject to eligibility, document verification, internal
        approval processes, applicable terms &amp; conditions, and may be modified or withdrawn
        without prior notice.
      </div>
    </PolicyPageShell>
  );
}

function OfferCard({ offer }) {
  return (
    <div
      data-testid={`offer-card-${offer.id}`}
      className={`group relative rounded-3xl p-7 border bg-gradient-to-br hover:-translate-y-1 transition-all ${
        offer.is_featured
          ? "border-[#D4AF37]/55 from-[#D4AF37]/[0.10] to-emerald-500/[0.04]"
          : "border-white/10 from-white/[0.04] to-white/[0.01] hover:border-[#D4AF37]/40"
      }`}
    >
      {offer.is_featured && (
        <span className="absolute -top-3 left-6 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#D4AF37] text-[#0A1F44] text-[10px] uppercase tracking-[0.18em] font-bold shadow-lg">
          <Flame size={11} /> Featured
        </span>
      )}
      <div className="flex items-center gap-2 text-[#F0D788]">
        <Sparkles size={18} />
        <span className="text-[10px] uppercase tracking-[0.22em]">{offer.category}</span>
      </div>
      <h3 className="mt-4 font-display text-xl text-white leading-tight">{offer.title}</h3>
      {offer.subtitle && <p className="mt-1.5 text-white/55 text-sm">{offer.subtitle}</p>}

      <div className="mt-5 grid grid-cols-2 gap-2.5 text-xs">
        {offer.interest_rate && (
          <Stat icon={TrendingDown} label="Interest" value={offer.interest_rate} />
        )}
        {offer.tenure && <Stat icon={Clock} label="Tenure" value={offer.tenure} />}
        {offer.discount && <Stat icon={Tag} label="Saving" value={offer.discount} />}
        {offer.valid_till && (
          <Stat icon={Calendar} label="Valid Till" value={offer.valid_till} />
        )}
      </div>

      {offer.description && (
        <p className="mt-5 text-white/65 text-sm leading-relaxed line-clamp-3">{offer.description}</p>
      )}

      <Link
        to={offer.cta_url || "/apply/loan"}
        data-testid={`offer-cta-${offer.id}`}
        className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full btn-gold text-sm font-semibold"
      >
        Claim this offer <ArrowRight size={14} />
      </Link>
    </div>
  );
}

function Stat({ icon: I, label, value }) {
  return (
    <div className="rounded-xl px-3 py-2.5 bg-white/[0.03] border border-white/5">
      <div className="flex items-center gap-1.5 text-white/40 text-[10px] uppercase tracking-wider">
        <I size={11} /> {label}
      </div>
      <div className="mt-0.5 text-white text-sm font-medium">{value}</div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-20">
      <Sparkles size={32} className="mx-auto text-[#F0D788]/55" />
      <h3 className="mt-4 font-display text-2xl text-white">No active offers right now</h3>
      <p className="mt-2 text-white/55 text-sm max-w-md mx-auto">
        We are cooking up the next round of festival deals. Meanwhile, our standard rates remain industry-leading.
      </p>
      <Link
        to="/apply/loan"
        className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full btn-gold text-sm font-semibold"
      >
        Apply for a loan <ArrowRight size={14} />
      </Link>
    </div>
  );
}



/* ─── DSA Monthly Achievements · Target Benefit ─────────────────── */

const DSA_DAILY_TIERS = [
  {
    id: "daily-5",
    files: "5 new direct-sponsor file log-in",
    schedule: "9:00 AM – 5:00 PM · every working day",
    minLoan: null,
    commissionNote: "Adv. Card Commission ₹250/-",
    lines: [{ label: "Total Card Commission", value: "₹1,250" }],
    total: "₹1,250",
  },
  {
    id: "daily-15",
    files: "15 new direct-sponsor file log-in",
    schedule: "9:00 AM – 5:00 PM · every working day",
    minLoan: "₹5 Lac (Card Commission ₹500/-)",
    commissionNote: "Higher card slab · loan-file commission unlocked",
    lines: [
      { label: "Adv. Card Commission", value: "₹4,500" },
      { label: "Loan File (Log-In) Commission @ 0.75%", value: "₹56,250" },
      { label: "Bike Fund", value: "₹50,000" },
    ],
    total: "₹1,10,750",
    highlight: true,
  },
];

const DSA_MONTHLY_TIERS = [
  {
    id: "monthly-270",
    files: "270 new direct-sponsor file log-in",
    schedule: "Payout aggregated monthly · credited to your bank",
    minLoan: "₹5 Lac (Card Commission ₹500/-)",
    commissionNote: "Monthly consistency slab · car-fund unlocked",
    lines: [
      { label: "Adv. Card Commission", value: "₹81,000" },
      { label: "Loan File (Log-In) Commission @ 0.80%", value: "₹10,80,000" },
      { label: "Car Fund", value: "₹5,00,000" },
    ],
    total: "₹15,80,000",
  },
  {
    id: "monthly-600",
    files: "600 new direct-sponsor file log-in",
    schedule: "Payout aggregated monthly · credited to your bank",
    minLoan: "₹5 Lac (Card Commission ₹500/-)",
    commissionNote: "Elite slab · 1% file commission + upgraded car fund",
    lines: [
      { label: "Adv. Card Commission", value: "₹2,00,000" },
      { label: "Loan File (Log-In) Commission @ 1.00%", value: "₹30,00,000" },
      { label: "Car Fund", value: "₹6,00,000" },
    ],
    total: "₹38,00,000",
    highlight: true,
  },
];

function DsaAchievements() {
  return (
    <section data-testid="dsa-achievements" className="mb-16">
      <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">
        DSA Monthly Achievements · Target Benefit
      </div>
      <h2 className="font-serif text-2xl md:text-3xl text-white mb-2">
        Daily &amp; Monthly Reward Structure
      </h2>
      <p className="text-white/60 text-sm max-w-2xl mb-8">
        Real, transparent earning targets — <strong className="text-white">daily and monthly
        payouts straight to your bank account</strong>. Reach the file log-in target,
        the payout is automatic.
      </p>

      {/* Daily group */}
      <div className="mb-10">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-full grid place-items-center bg-[#D4AF37]/15 border border-[#D4AF37]/35">
            <Sun size={15} className="text-[#F0D788]" />
          </div>
          <h3 className="font-serif text-xl text-white">1 · Daily Reward</h3>
          <span className="ml-2 text-[10px] uppercase tracking-wider text-[#F0D788]/80 bg-[#D4AF37]/10 border border-[#D4AF37]/25 rounded-full px-2 py-0.5">
            Payout credited daily
          </span>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {DSA_DAILY_TIERS.map((t) => (
            <TierCard key={t.id} tier={t} />
          ))}
        </div>
      </div>

      {/* Monthly group */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-full grid place-items-center bg-[#D4AF37]/15 border border-[#D4AF37]/35">
            <CalendarDays size={15} className="text-[#F0D788]" />
          </div>
          <h3 className="font-serif text-xl text-white">2 · Monthly Reward</h3>
          <span className="ml-2 text-[10px] uppercase tracking-wider text-[#F0D788]/80 bg-[#D4AF37]/10 border border-[#D4AF37]/25 rounded-full px-2 py-0.5">
            Aggregated · paid monthly
          </span>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {DSA_MONTHLY_TIERS.map((t) => (
            <TierCard key={t.id} tier={t} />
          ))}
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-[#D4AF37]/20 bg-[#0A1F44]/60 px-4 py-3 text-[11px] text-white/60 leading-relaxed">
        <strong className="text-white/85">Note:</strong> All figures shown are indicative
        gross earnings before applicable statutory deductions (TDS, GST as applicable).
        Card commission &amp; loan file commission rates are subject to product,
        eligibility criteria, and bank/NBFC disbursal confirmations. Fund payouts (Bike /
        Car) are processed after target achievement is verified &amp; documented by the
        Sahyog Finance corporate team.
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <Link
          to="/apply/dsa"
          data-testid="dsa-achievements-apply"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full btn-gold text-sm font-semibold"
        >
          <Trophy size={14} /> Become a DSA Partner
        </Link>
        <a
          href="tel:+917016975324"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full btn-outline-gold text-sm"
        >
          <Phone size={14} /> Discuss my targets · 70169 75324
        </a>
      </div>
    </section>
  );
}

function TierCard({ tier }) {
  return (
    <div
      data-testid={`dsa-tier-${tier.id}`}
      className={`rounded-2xl border p-5 md:p-6 transition ${
        tier.highlight
          ? "border-[#D4AF37]/55 bg-gradient-to-br from-[#0A1F44] via-[#12305c] to-[#04102B] shadow-[0_15px_40px_-20px_rgba(212,175,55,0.35)]"
          : "border-[#D4AF37]/20 bg-[#0A1F44]"
      }`}
    >
      <div className="text-white font-semibold text-base leading-snug">{tier.files}</div>
      <div className="mt-1.5 text-[11px] text-white/50 flex items-center gap-1.5">
        <Clock size={11} /> {tier.schedule}
      </div>
      {tier.minLoan && (
        <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-[#F0D788] bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-2.5 py-1">
          <ShieldCheck size={10} /> Min loan amount: {tier.minLoan}
        </div>
      )}
      <div className="mt-3 text-[11px] text-white/50 italic">{tier.commissionNote}</div>

      <div className="mt-4 border-t border-white/8 pt-4 space-y-2">
        {tier.lines.map((line) => (
          <div key={line.label} className="flex items-baseline justify-between gap-4 text-sm">
            <span className="text-white/70">{line.label}</span>
            <span className="font-semibold text-white tabular-nums">{line.value}</span>
          </div>
        ))}
      </div>

      <div
        className={`mt-4 rounded-xl p-3.5 flex items-center justify-between gap-3 ${
          tier.highlight
            ? "bg-gradient-to-r from-[#D4AF37]/25 to-[#F0D788]/10 border border-[#D4AF37]/45"
            : "bg-[#D4AF37]/10 border border-[#D4AF37]/25"
        }`}
      >
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#F0D788]">
          Total Income
        </div>
        <div className="flex items-center gap-1 font-serif text-xl md:text-2xl text-white tabular-nums">
          <IndianRupee size={16} className="text-[#F0D788] -mr-0.5" />
          {tier.total.replace("₹", "")}
        </div>
      </div>
    </div>
  );
}
